import BaseComponent from './base.type';
import { Size } from './common.type';
import { CommonFormGroupStyles, FormGroupMatrixProps } from './formGroup.type';

export enum QUESTION_CONTROL_TYPE {
  CHECKBOX = 'checkbox',
  DROPDOWN = 'dropdown',
  DATE = 'date', // datePicker
  INPUT = 'input',
  TEXTAREA = 'textarea',
  RADIO = 'radio', // radioButton
  MATRIX = 'matrix',
}

export enum QUESTIONNAIRE_TEMPLATE_VARIANT {
  BLANK = 'blank',
  CAREER = 'career',
  PERSONALITY = 'personality',
  LEADERSHIP = 'leadership',
  STRESS = 'stress',
  SKELETAL = 'skeletal',
  CUSTOMER = 'customer',
}

export enum COVER_IMAGE_TYPE {
  LOTTIE = 'lottie',
  IMAGE = 'image',
}

export enum COVER_IMAGE_CROPPING {
  COVER = 'cover',
  CONTAIN = 'contain',
}

export const QUESTION_WITH_TARGET = [
  QUESTION_CONTROL_TYPE.DROPDOWN,
  QUESTION_CONTROL_TYPE.RADIO,
];

export const QUESTION_WITH_TABLE = [
  QUESTION_CONTROL_TYPE.DROPDOWN,
  QUESTION_CONTROL_TYPE.RADIO,
  QUESTION_CONTROL_TYPE.CHECKBOX,
  QUESTION_CONTROL_TYPE.MATRIX,
];

export const QUESTION_WITH_WEIGHT = [
  QUESTION_CONTROL_TYPE.DROPDOWN,
  QUESTION_CONTROL_TYPE.RADIO,
  QUESTION_CONTROL_TYPE.MATRIX,
];

export interface IQuestionOption {
  id: string;
  label: string;
  value: string | number | boolean;
  /**
   * id of question to navigate to if this option is selected
   */
  target?: string;
  targetType?: 'screen' | 'question';
  point?: string | number;
  category?: string;
}

export interface IQuestionItem {
  id: string | number;
  index: number;
  question?: string;
  control: `${QUESTION_CONTROL_TYPE}`;
  type?: 'text' | 'number';
  multiline?: boolean;
  options?: IQuestionOption[];
  placeholder?: string;
  dateMode?: 'date' | 'time' | 'datetime';
  required?: boolean;
  fid?: string;
  /**
   * Use point system for option value. For dropdown and radio controls only.
   */
  weightedValueMode?: boolean;
  conditional?: boolean;
  note?: {
    enabled: boolean;
    text?: string;
  };
  /**
   * Start and end for matrix
   */
  range?: {
    start: number;
    end: number;
  };
  startText?: string;
  endText?: string;
  design?: FormGroupMatrixProps['design'];
  coverImage?: {
    enabled: boolean;
    type: `${COVER_IMAGE_TYPE}`;
    cropping: `${COVER_IMAGE_CROPPING}`;
    url?: string;
    lottieUrl?: string;
    loop?: boolean;
  };
  generalTarget?: {
    id: string;
    name: string;
    targetType: string;
    questionId: string;
  };
  name?: string;
  isScreen?: boolean;
  children?: any[];
}

export interface IQuestionCategory {
  id: string;
  name: string;
}

export interface IQuestionnaire extends BaseComponent {
  pageTemplateId: string;
  configs: {
    formType: string;
    successPage: string;
    questionMarker: string;
    progressBar: {
      enabled: boolean;
      variant: string;
    };
    submitButtonText?: string;
    /**
     * for canvas use only, determine which component is actively being edited
     */
    activeChildComponent?: string;
  };
  recommendationConfig?:
    | {
        databaseUuid: string;
        type: 'simple';
      }
    | {
        databaseUuid: string;
        matchingItemDatabaseUuid: string;
        type: 'complex';
      };
  isPreview: boolean;
  questions: IQuestionItem[];
  databaseUuid: string;
  /**
   * for canvas display, read-only
   */
  defaultQuestions?: IQuestionItem[];
  categoryDatabaseUuid: string;
  categories: IQuestionCategory[];
  screenUuid?: string;
  style: {
    backgroundColor?: string;
    fontFamily: string;
    fontSize: number;
    color: string;
    textAlign: string;
    questionMarker: {
      color: string;
      fontSize: number;
      backgroundColor: string;
      borderColor: string;
      borderRadius: number;
      borderWidth: number;
      borderStyle: string;
      size: Size;
    };
    question: {
      color: string;
      fontSize: number;
    };
    progressBar: {
      backgroundColor: string;
      bar: {
        backgroundColor: string;
      };
      size: Size;
    };
    progressBarNyanCat: {
      backgroundColor: string;
      bar: {
        size: Size;
        /**
         * internal style configs: minHeight, minWidth
         */
        minHeight?: number;
        minWidth?: number;
      };
      nyanCat: {
        size: Size;
        /**
         * internal style configs: minHeight, minWidth
         */
        minHeight?: number;
        minWidth?: number;
      };
    };
    progressStepper: {
      color: string;
      step: {
        activeColor: string;
        backgroundColor: string;
        size: Size;
      };
      line: {
        backgroundColor: string;
        borderBottomWidth: string;
      };
    };
    dropdown: CommonFormGroupStyles;
    datepicker: CommonFormGroupStyles;
    checkbox: Omit<CommonFormGroupStyles, 'size'> & {
      activeColor: string;
      inactiveColor: string;
    };
    note: {
      fontSize: number;
      color: string;
    };
    button: {
      backgroundColor: string;
      borderRadius: number;
      height: number;
      fontSize: number;
    };
  };
}
